import {
  doc,
  setDoc,
  updateDoc,
  deleteField,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { COLLECTIONS } from '@/lib/collections'
import type { MacroTargets, UserProfile } from '@/types'

/**
 * Merge-create the ksnap_users profile. { merge: true } is REQUIRED
 * (mirrors mobile FirestoreService.createUserProfile):
 * - firestore.rules pin the server-written scan counters and legacyPremium;
 *   a plain set() would try to delete them and be rejected.
 * - Re-creating for an existing account must never wipe the rest of the
 *   profile (streak, favorites refs, premium flag, migrated data refs).
 */
export async function createUserProfile(uid: string, data: Partial<UserProfile> = {}): Promise<void> {
  await setDoc(
    doc(db, COLLECTIONS.USERS, uid),
    {
      notificationsEnabled: true,
      isPremium: false,
      // Web signups skip mobile onboarding — seed the standard keto defaults.
      ketoStrictness: 'moderate',
      carbCountingMode: 'net',
      ...data,
      id: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}

export async function updateUserProfile(uid: string, partial: Partial<UserProfile>): Promise<void> {
  await setDoc(doc(db, COLLECTIONS.USERS, uid), { ...partial, updatedAt: serverTimestamp() }, { merge: true })
}

/** Replace macro targets wholesale (matches mobile keto-targets save). */
export async function saveMacroTargets(uid: string, targets: MacroTargets): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
    macroTargets: targets,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Precise dotted-path updates (e.g. 'optionalNutrients.showCalories').
 * Pass a value to set a field, or null to delete it.
 */
export async function updateProfileFields(
  uid: string,
  fields: Record<string, unknown | null>,
): Promise<void> {
  const updates: Record<string, unknown> = { updatedAt: serverTimestamp() }
  for (const [key, value] of Object.entries(fields)) {
    updates[key] = value === null ? deleteField() : value
  }
  await updateDoc(doc(db, COLLECTIONS.USERS, uid), updates)
}
